import React, {useMemo, useState} from 'react';
import {Link} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faBookmark,
    faClipboard, faExternalLink,
    faFileArrowUp,
    faInfoCircle,
    faRotateLeft,
    faTrashCan
} from "@fortawesome/free-solid-svg-icons";
import {generateBookmarklet, parseCountabilityBundle} from "ebird-mydata-reader";
import * as localforage from "localforage";
import {getRandomDashboardHeroImage} from "../../helpers/dashboardHeroImages";
import {
    CALIBRATION_BOOKMARKLET_NAME,
    STORED_COUNTABILITY_CALIBRATION_KEY,
    STORED_COUNTABILITY_CALIBRATION_VERSION
} from "./constants";
import {getCalibrationStartYear} from "./helpers";
import type {CalibrateDataProps, StoredCountabilityCalibration} from "./types";
import './calibrate-data.scss';

export default function CalibrateData({years, onCalibrationApplied, onCalibrationCleared}: CalibrateDataProps) {
    const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
    const [calibrationFileName, setCalibrationFileName] = useState('');
    const [calibrationStatus, setCalibrationStatus] = useState('');
    const [calibrationError, setCalibrationError] = useState('');
    const startYear = useMemo(() => getCalibrationStartYear(years), [years]);
    const bookmarklet = useMemo(() => generateBookmarklet(startYear), [startYear]);
    const calibrationHeroImage = useMemo(() => getRandomDashboardHeroImage(), []);

    const copyBookmarklet = async () => {
        try {
            await navigator.clipboard.writeText(bookmarklet);
            setCopyState('copied');
        } catch (error) {
            setCopyState('failed');
        }
    };

    const saveCalibrationFile = async (file: File) => {
        setCalibrationFileName(file.name);
        setCalibrationStatus('Reading calibration data...');
        setCalibrationError('');

        try {
            const rawBundle = await file.text();
            const calibration = parseCountabilityBundle(rawBundle);
            const payload: StoredCountabilityCalibration = {
                version: STORED_COUNTABILITY_CALIBRATION_VERSION,
                date: new Date().toISOString(),
                fileName: file.name,
                rawBundle,
                summary: {
                    region: calibration.region,
                    generatedAt: calibration.generatedAt,
                    hasLifeScope: Boolean(calibration.life),
                    yearCount: Object.keys(calibration.years).length,
                    diagnosticsCount: calibration.diagnostics.length
                }
            };

            await localforage.setItem(STORED_COUNTABILITY_CALIBRATION_KEY, payload);
            setCalibrationStatus(`Saved calibration data for ${payload.summary.region}. Reprocessing your sightings...`);
            await onCalibrationApplied(rawBundle, payload.date);
        } catch (error) {
            console.error(error);
            setCalibrationStatus('');
            setCalibrationError('Unable to apply that calibration file. Please upload the JSON file generated by the bookmarklet.');
        }
    };

    const onCalibrationFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        void saveCalibrationFile(file);
    };

    const onCalibrationFileDrop = (event: React.DragEvent<HTMLLabelElement>) => {
        event.preventDefault();

        const file = event.dataTransfer.files?.[0];
        if (!file) return;

        void saveCalibrationFile(file);
    };

    const clearCalibrationFile = async () => {
        setCalibrationFileName('');
        setCalibrationError('');
        setCalibrationStatus('Clearing calibration data...');

        try {
            await onCalibrationCleared();
            setCalibrationStatus('Calibration data cleared.');
        } catch (error) {
            console.error(error);
            setCalibrationStatus('');
            setCalibrationError('Unable to clear calibration data. Please try again.');
        }
    };

    return (
        <section className="calibrate-data" aria-labelledby="calibrate-data-title">
            <div className="calibrate-data__content">
                <p className="calibrate-data__eyebrow">Optional calibration</p>
                <h1 id="calibrate-data-title" className="calibrate-data__headline">Calibrate your eBird counts</h1>
                <div className="calibrate-data__copy">
                    <h2>Why do I need to do this?</h2>
                    <p><strong>I wish you didn&rsquo;t have to!</strong> Unfortunately, eBird doesn&rsquo;t mark sightings following its new exotic / naturalized species rules. This
                    step helps ensure that your sightings in BirdMap Explorer match what you see in eBird. You can absolutely skip this step but your
                    numbers might be a little off compared to eBird.</p>
                </div>

                <div className="calibrate-data__workflow">
                    <div className="calibrate-data__bookmarklet">
                        <p className="calibrate-data__bookmarklet-instruction">
                            <FontAwesomeIcon icon={faInfoCircle} aria-hidden="true" />{' '}
                            Drag this bookmarklet to your browser&rsquo;s bookmarks bar.
                        </p>
                        <a className="btn btn-primary calibrate-data__bookmarklet-button" href={bookmarklet}>
                            <FontAwesomeIcon icon={faBookmark} aria-hidden="true" />
                            {CALIBRATION_BOOKMARKLET_NAME}
                        </a>
                        <button type="button" className="calibrate-data__copy-bookmarklet" onClick={copyBookmarklet}>
                            <FontAwesomeIcon icon={faClipboard} aria-hidden="true" />
                            Copy Bookmarklet
                        </button>
                    </div>

                </div>

                <label
                    className="calibrate-data__drop-zone"
                    htmlFor="calibrationDataFile"
                    onDragOver={event => event.preventDefault()}
                    onDrop={onCalibrationFileDrop}
                >
                    <FontAwesomeIcon icon={faFileArrowUp} aria-hidden="true" />
                    <span>
                        <strong>{calibrationFileName || 'Upload calibration data'}</strong>
                        <span>Drop or select the JSON file generated by the bookmarklet.</span>
                    </span>
                    <input
                        type="file"
                        id="calibrationDataFile"
                        name="calibrationDataFile"
                        accept=".json,application/json"
                        onChange={onCalibrationFileChange}
                    />
                </label>
                {calibrationStatus ? <p className="calibrate-data__status">{calibrationStatus}</p> : null}
                {calibrationError ? <p className="calibrate-data__status calibrate-data__status--error">{calibrationError}</p> : null}

                <div className="calibrate-data__actions">
                    <Link className="btn btn-link calibrate-data__back-link" to="/dashboard">
                        <FontAwesomeIcon icon={faRotateLeft} aria-hidden="true" />
                        Back to Dashboard
                    </Link>
                    <button type="button" className="calibrate-data__clear-button" onClick={clearCalibrationFile}>
                        <FontAwesomeIcon icon={faTrashCan} aria-hidden="true" />
                        Clear calibration data
                    </button>
                </div>
                {copyState === 'copied' ? <p className="calibrate-data__status">Bookmarklet copied.</p> : null}
                {copyState === 'failed' ? <p className="calibrate-data__status calibrate-data__status--error">Copy failed. Drag the bookmarklet link instead.</p> : null}
            </div>

            <aside
                className="calibrate-data__hero"
                aria-label="Bird in a natural habitat"
                style={{'--calibrate-data-hero-image': `url("${calibrationHeroImage}")`} as React.CSSProperties}
            >
                <div className="calibrate-data__hero-card">
                    <div className="calibrate-data__feature">
                        <span className="calibrate-data__feature-marker" aria-hidden="true">1</span>
                        <span>
                            <strong className="calibrate-data__feature-title">Save the Bookmarklet</strong>
                            <span className="calibrate-data__feature-copy">Drag the bookmarklet to your browser&rsquo;s bookmarks bar. You&rsquo;ll only need to do this once.</span>
                        </span>
                    </div>
                    <div className="calibrate-data__feature">
                        <span className="calibrate-data__feature-marker" aria-hidden="true">2</span>
                        <span>
                            <strong className="calibrate-data__feature-title">Run it on <a className="calibrate-data__feature-link"
                                href="https://www.ebird.org/lifelist" target="_blank" rel="noreferrer">eBird.org/lifelist <FontAwesomeIcon icon={faExternalLink} /></a></strong>
                            <span className="calibrate-data__feature-copy">Open eBird while logged in, then click the saved bookmarklet.</span>
                        </span>
                    </div>
                    <div className="calibrate-data__feature">
                        <span className="calibrate-data__feature-marker" aria-hidden="true">3</span>
                        <span>
                            <strong className="calibrate-data__feature-title">Drag the calibration file here.</strong>
                            <span className="calibrate-data__feature-copy">The next step will apply that calibration file to your local BirdMap Explorer data.</span>
                        </span>
                    </div>
                </div>
            </aside>
        </section>
    );
}
